import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-construction-darkblue text-white py-12">
        <div className="container mx-auto px-4">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-construction-yellow hover:text-yellow-400 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <Shield size={48} className="text-construction-yellow" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">
                Privacy Policy
              </h1>
              <p className="text-gray-300 mt-2">
                GDPR Compliant | UK Data Protection Act 2018
              </p>
            </div>
          </div>
          <p className="text-gray-300 mt-4">
            Last Updated: February 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8 md:p-12">
            
            {/* Introduction */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4 flex items-center gap-2">
                <Shield size={24} className="text-construction-yellow" />
                1. Introduction
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>Construction Cards Helpline Limited</strong> (Company Number: 13678504), a company incorporated in the United Kingdom on 14th October 2021, is committed to protecting and respecting your privacy.
                </p>
                <p>
                  This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website, engage with our services, or contact us.
                </p>
                <p>
                  This policy applies to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Visitors to our website</li>
                  <li>Customers who book our services</li>
                  <li>Individuals who submit enquiries via phone, email, or contact forms</li>
                  <li>Anyone whose personal data we process in the course of providing our services</li>
                </ul>
                <p className="font-semibold">
                  We are the "Data Controller" under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                </p>
                <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-4 rounded-r-lg mt-4">
                  <p className="font-semibold text-construction-darkblue">
                    Our registered office address:
                  </p>
                  <p>124 City Road, London EC1V 2NX, United Kingdom</p>
                  <p className="mt-2">
                    <strong>Data Protection Contact:</strong> info@constructioncardshelpline.co.uk
                  </p>
                </div>
              </div>
            </div>

            {/* Information We Collect */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4 flex items-center gap-2">
                <FileText size={24} className="text-construction-yellow" />
                2. Information We Collect
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We collect and process the following categories of personal data:
                </p>
                
                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    A) Personal Identification Data
                  </h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Full name</li>
                    <li>Date of birth</li>
                    <li>National Insurance (NI) number</li>
                    <li>Contact telephone number</li>
                    <li>Email address</li>
                    <li>Home address and postcode</li>
                    <li>Photographic identification (copies retained for verification purposes)</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    B) Technical Data
                  </h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Internet Protocol (IP) address</li>
                    <li>Browser type and version</li>
                    <li>Device type and operating system</li>
                    <li>Time zone settings</li>
                    <li>Cookie data (see Section 9)</li>
                    <li>Pages visited and time spent on pages</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    C) Transaction Data
                  </h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Payment card details (processed securely via third-party payment processors)</li>
                    <li>Billing address</li>
                    <li>Purchase history</li>
                    <li>Service bookings and dates</li>
                  </ul>
                  <p className="mt-2 text-sm italic">
                    Note: We do not store full payment card details on our servers. Payments are processed by PCI-DSS compliant third-party providers.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    D) Communication Data
                  </h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Enquiry form submissions</li>
                    <li>Email correspondence</li>
                    <li>Phone call records (including recordings where notified)</li>
                    <li>WhatsApp or other messaging communications</li>
                    <li>Customer feedback and complaints</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    E) Employment & Qualification Data
                  </h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Current and previous employment details</li>
                    <li>Trade or occupation</li>
                    <li>Existing qualifications and certifications</li>
                    <li>Work experience information</li>
                    <li>NVQ portfolio evidence (photos, videos, documentation)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How We Collect Data */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">
                3. How We Collect Your Data
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We collect personal data through the following methods:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Direct Interactions:</strong> When you contact us via phone, email, WhatsApp, or complete online forms on our website</li>
                  <li><strong>Service Bookings:</strong> When you book a CSCS test, NVQ course, CPCS training, or other services</li>
                  <li><strong>Automated Technologies:</strong> Cookies and similar tracking technologies when you visit our website (see Section 9)</li>
                  <li><strong>Third Parties:</strong> From test centres, training providers, or awarding bodies when necessary to provide our services</li>
                  <li><strong>Public Sources:</strong> From publicly accessible sources such as Companies House when verifying business clients</li>
                </ul>
              </div>
            </div>

            {/* How We Use Personal Data */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">
                4. How We Use Your Personal Data
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We process your personal data for the following purposes, based on specific lawful grounds under UK GDPR:
                </p>

                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    Purpose 1: Service Delivery
                  </h3>
                  <p className="mb-2"><strong>Lawful Basis:</strong> Contract Performance</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Processing CSCS, CPCS, NPORS, and NVQ bookings</li>
                    <li>Arranging test dates and course enrolment</li>
                    <li>Communicating with you about your bookings</li>
                    <li>Sharing necessary information with test centres and training providers</li>
                    <li>Facilitating payment processing</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    Purpose 2: Identity Verification
                  </h3>
                  <p className="mb-2"><strong>Lawful Basis:</strong> Legal Obligation & Contract Performance</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Verifying your identity to comply with awarding body requirements</li>
                    <li>Preventing fraud and identity theft</li>
                    <li>Ensuring eligibility for requested qualifications</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    Purpose 3: Customer Support
                  </h3>
                  <p className="mb-2"><strong>Lawful Basis:</strong> Legitimate Interests</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Responding to enquiries and support requests</li>
                    <li>Resolving complaints</li>
                    <li>Providing guidance on qualification requirements</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    Purpose 4: Business Operations
                  </h3>
                  <p className="mb-2"><strong>Lawful Basis:</strong> Legitimate Interests</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Improving our services and website functionality</li>
                    <li>Internal record-keeping and administration</li>
                    <li>Analyzing service usage and customer satisfaction</li>
                    <li>Protecting against fraud and abuse</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-4">
                  <h3 className="font-bold text-construction-darkblue mb-3">
                    Purpose 5: Marketing Communications
                  </h3>
                  <p className="mb-2"><strong>Lawful Basis:</strong> Consent (where required) or Legitimate Interests</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Sending updates about our services</li>
                    <li>Providing information about relevant qualifications</li>
                    <li>Notifying you of special offers or new courses</li>
                  </ul>
                  <p className="mt-2 text-sm italic">
                    You can opt out of marketing communications at any time by clicking "unsubscribe" in emails or contacting us directly.
                  </p>
                </div>

                <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-4 rounded-r-lg">
                  <h4 className="font-bold text-construction-darkblue mb-2">
                    Processing National Insurance Numbers
                  </h4>
                  <p className="text-sm">
                    We process National Insurance numbers as they are required by CSCS, CPCS, and awarding bodies for identity verification and certification purposes. This processing is necessary for contract performance and compliance with legal obligations imposed by certification schemes.
                  </p>
                </div>
              </div>
            </div>

            {/* Sharing Your Data */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">
                5. Sharing Your Personal Data
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We may share your personal data with the following categories of recipients:
                </p>

                <div className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">Awarding Bodies & Certification Schemes</h4>
                    <p className="text-sm">CSCS, CPCS, NPORS, and NVQ awarding organisations require your personal data to process applications and issue certifications.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">Test Centres & Training Providers</h4>
                    <p className="text-sm">Accredited test centres and CITB-approved training providers need your details to administer tests and deliver courses.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">Payment Processors</h4>
                    <p className="text-sm">Secure third-party payment providers process transactions on our behalf. We do not store full card details.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">IT Service Providers</h4>
                    <p className="text-sm">Website hosting, email services, CRM systems, and cloud storage providers who support our operations.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">Legal & Regulatory Authorities</h4>
                    <p className="text-sm">Where required by law, court order, or regulatory obligation.</p>
                  </div>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mt-6">
                  <p className="font-bold text-red-700 mb-2">
                    Important: We Do Not Sell Your Data
                  </p>
                  <p className="text-sm text-gray-700">
                    Construction Cards Helpline Limited does not sell, rent, or trade your personal data to third parties for marketing purposes.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Security */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4 flex items-center gap-2">
                <Lock size={24} className="text-construction-yellow" />
                6. Data Security
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">Technical Measures</h4>
                    <ul className="text-sm space-y-1 list-disc pl-5">
                      <li>SSL/TLS encryption for data transmission</li>
                      <li>Secure server infrastructure</li>
                      <li>Regular security updates and patches</li>
                      <li>Firewall protection</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">Organisational Measures</h4>
                    <ul className="text-sm space-y-1 list-disc pl-5">
                      <li>Access restricted to authorised personnel only</li>
                      <li>Staff training on data protection</li>
                      <li>Confidentiality agreements with employees</li>
                      <li>Regular security audits</li>
                    </ul>
                  </div>
                </div>
                <p className="text-sm italic">
                  While we take all reasonable steps to protect your data, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
                </p>
              </div>
            </div>

            {/* Data Retention */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">
                7. Data Retention
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected and to comply with legal obligations.
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-construction-darkblue mb-3">Retention Periods:</h4>
                  <ul className="space-y-2">
                    <li><strong>Customer Records:</strong> 7 years from last transaction (HMRC requirement)</li>
                    <li><strong>Qualification Applications:</strong> Duration required by awarding bodies (typically 3-5 years)</li>
                    <li><strong>Marketing Consent:</strong> Until consent is withdrawn or 2 years of inactivity</li>
                    <li><strong>Website Analytics:</strong> 26 months (Google Analytics default)</li>
                    <li><strong>Enquiry Data:</strong> 2 years if no service booking made</li>
                  </ul>
                </div>
                <p>
                  After the retention period expires, personal data is securely deleted or anonymised in accordance with our data disposal procedures.
                </p>
              </div>
            </div>

            {/* Your Rights */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4 flex items-center gap-2">
                <Eye size={24} className="text-construction-yellow" />
                8. Your Data Protection Rights
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Under UK GDPR and the Data Protection Act 2018, you have the following rights:
                </p>

                <div className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">1. Right to Access</h4>
                    <p className="text-sm">Request a copy of the personal data we hold about you (Subject Access Request).</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">2. Right to Rectification</h4>
                    <p className="text-sm">Request correction of inaccurate or incomplete personal data.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">3. Right to Erasure ("Right to be Forgotten")</h4>
                    <p className="text-sm">Request deletion of your personal data in certain circumstances (subject to legal retention obligations).</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">4. Right to Restrict Processing</h4>
                    <p className="text-sm">Request that we limit how we use your data in specific situations.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">5. Right to Data Portability</h4>
                    <p className="text-sm">Receive your data in a structured, commonly used format and transfer it to another controller.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">6. Right to Object</h4>
                    <p className="text-sm">Object to processing based on legitimate interests or for direct marketing purposes.</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-bold text-construction-darkblue mb-2">7. Right to Withdraw Consent</h4>
                    <p className="text-sm">Withdraw consent for marketing communications or other consent-based processing at any time.</p>
                  </div>
                </div>

                <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-4 rounded-r-lg mt-6">
                  <h4 className="font-bold text-construction-darkblue mb-2">
                    How to Exercise Your Rights
                  </h4>
                  <p className="text-sm mb-2">
                    To exercise any of these rights, please contact us:
                  </p>
                  <p className="text-sm">
                    <strong>Email:</strong> info@constructioncardshelpline.co.uk<br />
                    <strong>Phone:</strong> +44 7848 482492<br />
                    <strong>Post:</strong> 124 City Road, London EC1V 2NX
                  </p>
                  <p className="text-sm mt-3">
                    We will respond to requests within one month. In complex cases, this may be extended by two months.
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mt-6">
                  <h4 className="font-bold text-blue-700 mb-2">
                    Right to Complain
                  </h4>
                  <p className="text-sm text-gray-700">
                    If you are unhappy with how we handle your personal data, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Website:</strong> <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.ico.org.uk</a><br />
                    <strong>Phone:</strong> 0303 123 1113
                  </p>
                </div>
              </div>
            </div>

            {/* Cookies */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">
                9. Cookies
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Our website uses cookies and similar tracking technologies to enhance your browsing experience and analyze website traffic.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-construction-darkblue mb-3">Types of Cookies We Use:</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <h5 className="font-semibold">Essential Cookies</h5>
                      <p className="text-sm">Required for the website to function properly (e.g., session management, security).</p>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold">Analytical Cookies</h5>
                      <p className="text-sm">Help us understand how visitors use our website (e.g., Google Analytics).</p>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold">Marketing Cookies</h5>
                      <p className="text-sm">Track your activity across websites to deliver relevant advertising (with your consent).</p>
                    </div>
                  </div>
                </div>

                <p>
                  You can control and/or delete cookies through your browser settings. However, disabling cookies may affect website functionality.
                </p>

                <p>
                  For more information, please see our <Link to="/cookies" className="text-construction-yellow font-semibold hover:underline">Cookie Policy</Link>.
                </p>
              </div>
            </div>

            {/* Third-Party Links */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">
                10. Third-Party Links
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Our website may contain links to external websites operated by third parties (e.g., test centre websites, awarding body portals, social media platforms).
                </p>
                <p>
                  <strong>We are not responsible for the privacy practices or content of these external sites.</strong> We recommend reviewing the privacy policies of any third-party websites you visit.
                </p>
              </div>
            </div>

            {/* Changes to Policy */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-4">
                11. Changes to This Privacy Policy
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or operational needs.
                </p>
                <p>
                  Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
                </p>
                <p>
                  For significant changes that materially affect how we process your data, we will notify you via email or prominent website notice where appropriate.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-construction-darkblue text-white rounded-lg p-6">
              <h3 className="text-2xl font-bold mb-4">12. Contact Us</h3>
              <p className="mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal data, please contact us:
              </p>
              <div className="space-y-2">
                <p><strong>Data Protection Contact:</strong></p>
                <p>Construction Cards Helpline Limited</p>
                <p>124 City Road, London EC1V 2NX, United Kingdom</p>
                <p><strong>Email:</strong> info@constructioncardshelpline.co.uk</p>
                <p><strong>Phone:</strong> +44 7848 482492</p>
                <p><strong>Company Number:</strong> 13678504</p>
              </div>
            </div>

            {/* Footer Note */}
            <div className="mt-10 pt-6 border-t border-gray-200 text-sm text-gray-600">
              <p className="font-semibold mb-2">
                Compliance Statement:
              </p>
              <p>
                This Privacy Policy complies with the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018, and the Privacy and Electronic Communications Regulations (PECR).
              </p>
              <p className="mt-4">
                <strong>Last Updated:</strong> February 2026
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
